import i18n from 'i18next'
import type { BackendModule, ResourceKey } from 'i18next'
import { initReactI18next } from 'react-i18next'
import useI18nStore from '@/store/i18nStore'

// 初始语言以 i18nStore 为唯一真源（persist 同步读取 localStorage，未持久化时取 store
// 默认值）。之前这里另按浏览器语言推断，与 store 默认值不一致时 App 挂载后会再
// changeLanguage 一次；语言包按需加载后，这会让首访用户多下载一整份语言包（#1224）。
//
// Jest 固定 en-US：沿用改造前 jsdom navigator.language 推断出的语言，既有测试的文案
// 断言都基于英文包。
const initialLocale = process.env.NODE_ENV === 'test' ? 'en-US' : useI18nStore.getState().locale

// 三份语言包各约 310 KB，同步 import 时占主包 60%（#1224），改为按语言拆 chunk、
// 只加载当前语言。
//
// Jest 里保持同步加载：大量测试在 render 后立即断言翻译文案，异步加载会让它们
// 拿到裸 key。生产构建中 NODE_ENV 被编译为常量，test 分支连同其中的 require
// 一起被消除，不会把语言包打回主包——require 必须写在该 if 分支内，抽成独立
// 函数会被 webpack 当作无条件依赖打包。
const lazyLocaleBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(language, _namespace, callback) {
    if (process.env.NODE_ENV === 'test') {
      /* eslint-disable @typescript-eslint/no-var-requires */
      const bundled: Record<string, ResourceKey> = {
        'zh-CN': require('./lang/zh-CN.json'),
        'zh-TW': require('./lang/zh-TW.json'),
        'en-US': require('./lang/en-US.json'),
      }
      /* eslint-enable @typescript-eslint/no-var-requires */
      // 同步回调 + initImmediate:false，init 返回前资源已就绪
      callback(null, bundled[language] ?? {})
      return
    }
    import(/* webpackChunkName: "locale-[request]" */ `./lang/${language}.json`).then(
      (module) => callback(null, module.default),
      // 第二个参数为 true 时 i18next 会自动重试（默认最多 5 次），兜住发版瞬间
      // 或网络抖动导致的 chunk 加载失败
      (error) => callback(error, true)
    )
  },
}

/**
 * 当前语言包加载完成（或重试耗尽）后 resolve。入口在它之后再挂载 React，
 * 避免首屏闪出裸 key。
 */
export const i18nReady = i18n
  .use(lazyLocaleBackend)
  .use(initReactI18next)
  .init({
    lng: initialLocale,
    // 三语 key 集合由 src/locales/localeKeyParity.test.ts 锁步保证，无需回退；
    // 设了 fallbackLng 会让非该语言的用户额外下载一整份语言包
    fallbackLng: false,
    defaultNS: 'translation',
    interpolation: { escapeValue: false },
    initImmediate: process.env.NODE_ENV !== 'test',
    // Jest 预载三语：测试常用 getFixedT('zh-CN') 等取非当前语言的文案
    preload: process.env.NODE_ENV === 'test' ? ['zh-CN', 'zh-TW', 'en-US'] : false,
  })

export default i18n
